import api from "./api";
import { UserObj, SalesReportObj } from "../GlobalTypes";
import { PaintLeadTimesObj } from "../components/dealerZone/LeadTimes/useLeadTimesSelector";
import { CustomEventState } from "../components/dealerZone/Calendar/CustomEventModal/CustomEventModal";
import { EventDetailsState } from "../components/dealerZone/Calendar/EventDetails/EventDetails";
import {
  FilterObj,
  EntryDataDoc,
} from "../components/dealerZone/Cutsheet/useCutsheetSelector";

type SearchQuotesArg = {
  searchTerm: string;
  status: string;
  searchBy: string;
  pageNumber: number;
  rowsPerPage: number;
  showArchive?: boolean;
};

async function searchAllQuotes(searchArg: SearchQuotesArg) {
  return api.get("/api/searchAll", {
    params: searchArg,
  });
}

type GetQuotesArg = {
  pageNumber: number;
  rowsPerPage: number;
  status: string;
  showArchive?: boolean;
};

async function getAllQuotes(quotesArg: GetQuotesArg) {
  return await api.get("/api/getAllQuotes", {
    params: quotesArg,
  });
}

async function postHideTips() {
  return await api.put("/api/hideTips");
}

async function getLogo() {
  return await api.get("/api/getDealerLogo");
}

async function postStatusChange(
  showTab: string,
  selected: string[],
  newStatus: string
) {
  return await api.put("/api/changeStatus", {
    showTab,
    selected,
    newStatus,
    dateOrdered: new Date(),
  });
}

async function getUser(id: string) {
  return await api.get("/api/userInfo", {
    params: { id },
  });
}

async function postCloneQuote(quoteNumber: number, userId: string) {
  return await api.post("/api/cloneQuote", {
    quoteNumber,
    userId,
  });
}

async function postGlassOrderDate(selected: string[], date: string) {
  return await api.put("/api/addGlassOrderDate", {
    selected,
    date,
  });
}

async function getLabelData(salesOrder: string) {
  return await api.post(
    "/api/labelData",
    {
      salesOrder,
    },
    { responseType: "blob" }
  );
}

async function getDeliveryDates(month: number, year: number) {
  return await api.get("/api/getDeliveryDates", {
    params: { month, year },
  });
}

async function getEventDetails(id: string) {
  return await api.get("/api/eventDetails", {
    params: { id },
  });
}

async function getEventDetailsService(id: string) {
  return await api.get("/api/eventDetailsService", {
    params: { id },
  });
}

async function getCustomEventDetails(id: string) {
  return await api.get("/api/customEventDetails", {
    params: { id },
  });
}

type EventDetailsArg = {
  id: string;
  name: string | undefined;
  phone: string | undefined;
  calendarNotes: string | undefined;
  confirmDelivery?: boolean;
};

async function addEventDetails(eventArg: EventDetailsArg) {
  return await api.post("/api/eventDetails", eventArg);
}

async function searchCalSO(salesOrder: string) {
  return await api.post("/api/searchCalendarSO", { salesOrder });
}

type ScheduleArg = {
  ids: string[];
  date: Date;
  time: string | undefined;
  calendarYear: number;
  calendarMonth: number;
  sendEmail?: boolean;
  hasCustomEvent?: boolean;
};

async function setSchedule(schedArg: ScheduleArg) {
  return await api.post("/api/setSchedule", schedArg);
}

async function setCustomEvent(eventObj: CustomEventState) {
  return await api.post("/api/setEvent", { eventObj });
}

async function setCustomEventDetails(customArg: {
  id: string;
  eventObj: EventDetailsState;
  confirmDelivery: boolean;
}) {
  return await api.post("/api/setEventDetails", customArg);
}

async function changeStatusToDelivered(statusArg: {
  ids: string[];
  calendarYear: number;
  calendarMonth: number;
  calendarDay: number;
  hasCustomEvent?: boolean;
}) {
  return await api.post("/api/changeStatusToDelivered", statusArg);
}

async function createReport(form: SalesReportObj) {
  return await api.post("/api/createReport", { form });
}

async function searchReports(reportsArg: {
  page: number;
  rowsPerPage: number;
  searchBy: string;
  searchTerm: string;
}) {
  return await api.get("/api/searchReportTable", {
    params: reportsArg,
  });
}

async function getReports(page: number, rowsPerPage: number) {
  return await api.get("/api/salesReportTable", {
    params: {
      page,
      rowsPerPage,
    },
  });
}

async function viewReport(id: string) {
  return await api.get("/api/viewReport", {
    params: {
      id,
    },
  });
}

async function uploadCsv(formData: FormData) {
  return await api.post("/api/uploadCsv", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

async function getGlassDetails(searchTerm: string, filterBy: string) {
  return await api.get("/api/glassDetails", {
    params: {
      searchTerm,
      filterBy,
    },
  });
}

async function getGlassOrderSheet(quoteIds: string[], date: string | Date) {
  return await api.get("/api/glassOrderSheet", {
    params: { quoteIds, date },
  });
}

async function getLeadTimes() {
  return await api.get("/api/leadTimes");
}

async function getPaintLeadTimes() {
  return await api.get("/api/paintLeadTimes");
}

async function updatePaintLeadTimes(paintLead: PaintLeadTimesObj) {
  return await api.post("/api/admin/changePaintLeadTimes", {
    state: paintLead,
  });
}

async function archiveQuotes(
  ids: string[],
  archiveReason: string,
  archiveReasonTextBox: string
) {
  return await api.post("/api/archive", {
    ids,
    archiveReason,
    archiveReasonTextBox,
  });
}

async function unArchiveQuotes(ids: string[]) {
  return await api.post("/api/unarchive", { ids });
}

async function getCustomerList(
  page: number,
  rowsPerPage: number,
  showActive?: string
) {
  return await api.get("/api/customerList", {
    params: { page, rowsPerPage, activeStatus: showActive },
  });
}

async function searchCustomerList(listArg: {
  page: number;
  rowsPerPage: number;
  filterBy: string;
  searchTerm: string;
  activeStatus: string;
}) {
  return await api.get("/api/searchCustomerList", {
    params: listArg,
  });
}

async function getClipboardEmails(clipArg: {
  filterBy: string;
  searchTerm: string;
  activeStatus: string;
  ids: (string | undefined)[];
}) {
  return await api.get("/api/clipboardEmails", {
    params: clipArg,
  });
}

async function postInactiveStatus(statusArg: {
  ids: (string | undefined)[];
  activeStatus: string;
  inactiveReason?: string;
}) {
  return await api.post("/api/inactiveStatus", statusArg);
}

async function updateUser(
  ids: (string | undefined)[],
  userInfo: Partial<UserObj>
) {
  return await api.post("/api/updateUser", {
    ids,
    userInfo,
  });
}

async function uploadSaberis(saberisArg: {
  quoteId: string;
  username: string;
  password: string;
}) {
  return await api.post("/api/uploadSaberis", saberisArg);
}

async function editCutsheetData(cutsheetArg: {
  cutsheetId: string;
  doorType: string;
  typeOfData: string;
  data: Omit<EntryDataDoc, "_id">[];
}) {
  return await api.post("/api/editCutsheetData", cutsheetArg);
}

async function getCutsheetData(filter: FilterObj) {
  return await api.post("/api/getCutsheetData", { filter });
}

async function uploadCutsheetImage(formData: FormData) {
  return await api.post("/api/uploadCutsheetImage", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

async function updateCutsheetPart(cutsheetArg: {
  typeOfData: string;
  material: string;
  part: string;
  newPartsData: EntryDataDoc;
}) {
  return await api.post("/api/updateCutsheetsWithPartLetter", cutsheetArg);
}

export {
  editCutsheetData,
  getCutsheetData,
  uploadCutsheetImage,
  uploadSaberis,
  updateUser,
  postInactiveStatus,
  getClipboardEmails,
  getCustomerList,
  searchCustomerList,
  archiveQuotes,
  unArchiveQuotes,
  getGlassOrderSheet,
  getGlassDetails,
  uploadCsv,
  createReport,
  changeStatusToDelivered,
  setCustomEventDetails,
  getCustomEventDetails,
  setCustomEvent,
  setSchedule,
  searchCalSO,
  addEventDetails,
  getEventDetails,
  getLabelData,
  searchAllQuotes,
  getAllQuotes,
  postHideTips,
  getLogo,
  postStatusChange,
  getUser,
  postCloneQuote,
  postGlassOrderDate,
  getDeliveryDates,
  getLeadTimes,
  getPaintLeadTimes,
  updatePaintLeadTimes,
  searchReports,
  getReports,
  viewReport,
  getEventDetailsService,
  updateCutsheetPart,
};
